/**
 * Course data hooks (free astrology course). Server: services/astro-bot/src/api/routes/course-student.ts.
 *
 * Errors carry the server's `error` code in `.code` — branch on that, not on the status
 * (write limit: 429 {error:'rate_limited'}; no attempts left: 429 {error:'attempts_exhausted'}).
 * Bodiless POSTs send `{}` with a JSON content type (an empty JSON body is a 400).
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';
import type { CourseStateDto, ExamDto, LessonDto, QuizAnswerResult, SelfMark } from '../types/course';

export class CourseApiError extends Error {
  constructor(
    public status: number,
    /** The `error` field of the response body, or '' when there was none. */
    public code: string,
    public body: Record<string, unknown>,
  ) {
    super(`course_api_failed: ${status} ${code}`);
    this.name = 'CourseApiError';
  }
}

async function courseRequest<T>(jwt: string | null, path: string, method = 'GET', payload?: unknown): Promise<T> {
  if (!jwt) throw new Error('not_authenticated');
  const init: RequestInit = { method };
  if (method !== 'GET') {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(payload ?? {});
  }
  const res = await apiFetch(`/api/v1/course${path}`, jwt, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new CourseApiError(res.status, typeof body.error === 'string' ? body.error : '', body);
  }
  return (await res.json()) as T;
}

const COURSE_KEY = ['course'] as const;
const lessonKey = (code: string) => ['course', 'lesson', code] as const;
const examKey = (code: string) => ['course', 'exam', code] as const;

/** Refetch only the course state, not the lesson/exam queries that share the ['course'] prefix. */
function invalidateCourse(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: COURSE_KEY, exact: true });
}

/** Details of a 409 `incomplete` answer to "finish the lesson"; null for any other error. */
export function incompleteDetails(err: unknown): { missingExercises: string[]; missingQuiz: string[] } | null {
  if (!(err instanceof CourseApiError) || err.status !== 409 || err.code !== 'incomplete') return null;
  const strings = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)) : []);
  return { missingExercises: strings(err.body.missingExercises), missingQuiz: strings(err.body.missingQuiz) };
}

export function useCourseState() {
  const jwt = useAuth((s) => s.jwt);
  return useQuery<CourseStateDto, Error>({
    queryKey: COURSE_KEY,
    queryFn: () => courseRequest<CourseStateDto>(jwt, ''),
    enabled: !!jwt,
    staleTime: 30_000,
    retry: (n, err) => !(err instanceof CourseApiError) && n < 2,
  });
}

export function useStartCourse() {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  return useMutation<CourseStateDto, Error, { source: string }>({
    mutationFn: (v) => courseRequest<CourseStateDto>(jwt, '/start', 'POST', { source: v.source }),
    onSuccess: (data) => {
      qc.setQueryData(COURSE_KEY, data);
    },
  });
}

export function useLesson(code: string) {
  const jwt = useAuth((s) => s.jwt);
  return useQuery<LessonDto, Error>({
    queryKey: lessonKey(code),
    queryFn: () => courseRequest<LessonDto>(jwt, `/lessons/${encodeURIComponent(code)}`),
    enabled: !!jwt && !!code,
    retry: (n, err) => !(err instanceof CourseApiError) && n < 2,
  });
}

export function useSaveAnswer(code: string) {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  return useMutation<LessonDto, Error, { exerciseId: string; text: string }>({
    mutationFn: (v) => courseRequest<LessonDto>(jwt, `/lessons/${encodeURIComponent(code)}/exercises/${encodeURIComponent(v.exerciseId)}`, 'PUT', { text: v.text }),
    onSuccess: (lesson) => {
      qc.setQueryData(lessonKey(code), lesson);
    },
  });
}

export function useMarkAnswer(code: string) {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  return useMutation<LessonDto, Error, { exerciseId: string; mark: SelfMark }>({
    mutationFn: (v) => courseRequest<LessonDto>(jwt, `/lessons/${encodeURIComponent(code)}/exercises/${encodeURIComponent(v.exerciseId)}/mark`, 'POST', { mark: v.mark }),
    onSuccess: (lesson) => {
      qc.setQueryData(lessonKey(code), lesson);
    },
  });
}

export function useAnswerQuiz(code: string) {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  return useMutation<QuizAnswerResult, Error, { quizId: string; optionIndex: number }>({
    mutationFn: (v) => courseRequest<QuizAnswerResult>(jwt, `/lessons/${encodeURIComponent(code)}/quiz/${encodeURIComponent(v.quizId)}`, 'POST', { optionIndex: v.optionIndex }),
    onSuccess: (res) => {
      qc.setQueryData(lessonKey(code), res.lesson);
    },
  });
}

export function useCompleteLesson(code: string) {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  return useMutation<CourseStateDto, Error, void>({
    mutationFn: () => courseRequest<CourseStateDto>(jwt, `/lessons/${encodeURIComponent(code)}/complete`, 'POST', {}),
    onSuccess: async (data) => {
      qc.setQueryData(COURSE_KEY, data);
      await qc.invalidateQueries({ queryKey: lessonKey(code), exact: true });
    },
  });
}

const EXAM_POLL_MS = 5000;
const hasPending = (exam: ExamDto | undefined): boolean => !!exam?.attempts.some((a) => a.status === 'pending');

/** The exam. While an attempt waits for the AI check it is refetched every 5 s; polling stops by itself. */
export function useExam(code: string) {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  return useQuery<ExamDto, Error>({
    queryKey: examKey(code),
    queryFn: async () => {
      const wasPending = hasPending(qc.getQueryData<ExamDto>(examKey(code)));
      const fresh = await courseRequest<ExamDto>(jwt, `/exams/${encodeURIComponent(code)}`);
      // The check finished: the course page (statuses, next block) changed too.
      if (wasPending && !hasPending(fresh)) void invalidateCourse(qc);
      return fresh;
    },
    refetchInterval: (query) => (hasPending(query.state.data) ? EXAM_POLL_MS : false),
    enabled: !!jwt && !!code,
    retry: (n, err) => !(err instanceof CourseApiError) && n < 2,
  });
}

export function useSubmitExam(code: string) {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  return useMutation<ExamDto, Error, { answers: Record<string, string> }>({
    mutationFn: (v) => courseRequest<ExamDto>(jwt, `/exams/${encodeURIComponent(code)}/attempts`, 'POST', { answers: v.answers }),
    onSuccess: async (data) => {
      qc.setQueryData(examKey(code), data);
      await invalidateCourse(qc); // the state changes (exam pending); the exam itself is already fresh
    },
  });
}

/** «Слишком часто»: the write limit (429 rate_limited, or any 429 that is not «no attempts left»). */
export function isTooOften(err: unknown): boolean {
  if (!(err instanceof CourseApiError)) return false;
  return err.code === 'rate_limited' || (err.status === 429 && err.code !== 'attempts_exhausted');
}

/** Comparison keys are returned only after the server persists every answer. */
export function useSaveExamSelfCheck(code: string) {
  const jwt = useAuth((s) => s.jwt);
  return useMutation<import('../types/course').ExamSelfCheckDto, Error, import('../types/course').SelfCheckInput>({
    mutationFn: (input) => courseRequest(jwt, `/exams/${encodeURIComponent(code)}/self-checks`, 'POST', input),
  });
}

export function useExamSelfCheck(code: string, id: string | null) {
  const jwt = useAuth((s) => s.jwt);
  return useQuery<import('../types/course').ExamSelfCheckDto, Error>({
    queryKey: ['course', 'self-check', code, id], enabled: !!jwt && !!id, retry: false,
    queryFn: () => courseRequest(jwt, `/exams/${encodeURIComponent(code)}/self-checks/${encodeURIComponent(id!)}`),
  });
}

export function useDecideExamSelfCheck(code: string) {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  return useMutation<import('../types/course').ExamAttemptDto, Error, { id: string; mark: SelfMark; requestId: string }>({
    mutationFn: ({ id, ...input }) => courseRequest(jwt, `/exams/${encodeURIComponent(code)}/self-checks/${encodeURIComponent(id)}/decision`, 'POST', input),
    onSuccess: async (data, { id }) => {
      qc.setQueryData<import('../types/course').ExamSelfCheckDto>(['course', 'self-check', code, id], (saved) => saved ? { ...saved, completedAttempt: data } : saved);
      await Promise.all([invalidateCourse(qc), qc.invalidateQueries({ queryKey: examKey(code), exact: true })]);
    },
  });
}
